import type {
  TreeRuntimeConvergenceLiveProjection,
} from './TreeRuntimeConvergenceEvidenceProjection';
import {
  sanitizeTreeRuntimeSelectorDiagnostics,
} from './TreeRuntimeSelectorDiagnostics';
import {
  sanitizeTreeRuntimeSelectorDiscrepancyClassification,
} from './TreeRuntimeSelectorDiscrepancyClassification';
import {
  sanitizeTreeRuntimeSelectorDiscrepancySourceClassification,
} from './TreeRuntimeSelectorDiscrepancySourceClassification';

export const TREE_RUNTIME_CONVERGENCE_SESSION_KEY =
  'kinscircle:tree-runtime-convergence:v1';

export const TREE_RUNTIME_CONVERGENCE_SESSION_SCHEMA =
  1 as const;

export const TREE_RUNTIME_CONVERGENCE_MAX_AGE_MS =
  10 * 60 * 1000;

export interface TreeRuntimeConvergenceSessionEnvelope {
  readonly schema:
    typeof TREE_RUNTIME_CONVERGENCE_SESSION_SCHEMA;
  readonly storedAt: string;
  readonly projection:
    TreeRuntimeConvergenceLiveProjection;
}

const validStatus =
  (
    value: unknown,
  ): value is
    TreeRuntimeConvergenceLiveProjection['status'] =>
    value === 'READY' ||
    value === 'DEGRADED' ||
    value === 'UNSAFE';

const validReadiness =
  (
    value: unknown,
  ): value is
    TreeRuntimeConvergenceLiveProjection['enforcementReadiness'] =>
    value === 'REVIEW_ELIGIBLE' ||
    value === 'BLOCKED_DEGRADED' ||
    value === 'BLOCKED_UNSAFE';

const validAction =
  (
    value: unknown,
  ): value is
    NonNullable<
      TreeRuntimeConvergenceLiveProjection['containmentAction']
    > =>
    value === 'RENDER_CANONICAL' ||
    value === 'RENDER_WITH_WARNING' ||
    value ===
      'SUPPRESS_UNCERTIFIED_GEOMETRY';

const finiteOrNull =
  (
    value: unknown,
  ): number | null =>
    value === null ||
    (
      typeof value === 'number' &&
      Number.isFinite(value)
    )
      ? value as number | null
      : null;

export const sanitizeTreeRuntimeConvergenceProjection =
  (
    candidate: unknown,
  ): TreeRuntimeConvergenceLiveProjection | null => {
    if (
      !candidate ||
      typeof candidate !== 'object'
    ) {
      return null;
    }

    const value =
      candidate as Record<string, unknown>;

    if (
      !validStatus(value.status) ||
      !validReadiness(
        value.enforcementReadiness,
      ) ||
      !validAction(
        value.containmentAction,
      ) ||
      typeof value.observedAt !== 'string' ||
      !Array.isArray(value.blockers)
    ) {
      return null;
    }

    const blockers =
      value.blockers.every(
        blocker =>
          typeof blocker === 'string' &&
          blocker.length > 0 &&
          blocker.length <= 100,
      )
        ? Object.freeze([
            ...new Set(
              value.blockers as string[],
            ),
          ])
        : null;

    if (!blockers) {
      return null;
    }

    const selectorDiagnostics =
      sanitizeTreeRuntimeSelectorDiagnostics(
        value.selectorDiagnostics,
      );

    if (!selectorDiagnostics) {
      return null;
    }

    const selectorDiscrepancyClassification =
      sanitizeTreeRuntimeSelectorDiscrepancyClassification(
        value.selectorDiscrepancyClassification,
      );

    if (!selectorDiscrepancyClassification) {
      return null;
    }

    const selectorDiscrepancySourceClassification =
      sanitizeTreeRuntimeSelectorDiscrepancySourceClassification(
        value.selectorDiscrepancySourceClassification,
      );

    if (!selectorDiscrepancySourceClassification) {
      return null;
    }

    const convergenceStatus =
      value.convergenceStatus === 'READY' ||
      value.convergenceStatus === 'BLOCKED'
        ? value.convergenceStatus
        : null;

    if (!convergenceStatus) {
      return null;
    }

    return Object.freeze({
      status: value.status,
      enforcementReadiness:
        value.enforcementReadiness,
      observed: true,
      observedAt: value.observedAt,
      convergenceStatus,
      containmentAction:
        value.containmentAction,
      blockers,
      blockerCount:
        finiteOrNull(
          value.blockerCount,
        ) ??
        blockers.length,
      productionVisibleMemberCount:
        finiteOrNull(
          value.productionVisibleMemberCount,
        ),
      missingProjectionCount:
        finiteOrNull(
          value.missingProjectionCount,
        ),
      missingGeometryRoleCount:
        finiteOrNull(
          value.missingGeometryRoleCount,
        ),
      missingConnectorCount:
        finiteOrNull(
          value.missingConnectorCount,
        ),
      selectorDiagnostics,
      selectorDiscrepancyClassification,
      selectorDiscrepancySourceClassification,
      readOnly: true,
      enforcementExecuted: false,
      repairExecuted: false,
    });
  };

export const writeTreeRuntimeConvergenceSessionEvidence =
  (
    projection:
      TreeRuntimeConvergenceLiveProjection,
  ): boolean => {
    if (
      typeof window === 'undefined' ||
      !projection.observed
    ) {
      return false;
    }

    const sanitized =
      sanitizeTreeRuntimeConvergenceProjection(
        projection,
      );

    if (!sanitized) {
      return false;
    }

    const envelope:
      TreeRuntimeConvergenceSessionEnvelope =
      Object.freeze({
        schema:
          TREE_RUNTIME_CONVERGENCE_SESSION_SCHEMA,
        storedAt:
          new Date().toISOString(),
        projection: sanitized,
      });

    try {
      window.sessionStorage.setItem(
        TREE_RUNTIME_CONVERGENCE_SESSION_KEY,
        JSON.stringify(envelope),
      );

      return true;
    } catch {
      return false;
    }
  };

export const readTreeRuntimeConvergenceSessionEvidence =
  (
    nowMs:
      number = Date.now(),
  ): TreeRuntimeConvergenceLiveProjection | null => {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const raw =
        window.sessionStorage.getItem(
          TREE_RUNTIME_CONVERGENCE_SESSION_KEY,
        );

      if (!raw) {
        return null;
      }

      const envelope =
        JSON.parse(raw) as
          Partial<
            TreeRuntimeConvergenceSessionEnvelope
          >;

      if (
        envelope.schema !==
          TREE_RUNTIME_CONVERGENCE_SESSION_SCHEMA ||
        typeof envelope.storedAt !==
          'string'
      ) {
        window.sessionStorage.removeItem(
          TREE_RUNTIME_CONVERGENCE_SESSION_KEY,
        );

        return null;
      }

      const storedAtMs =
        Date.parse(envelope.storedAt);

      if (
        !Number.isFinite(storedAtMs) ||
        nowMs - storedAtMs < 0 ||
        nowMs - storedAtMs >
          TREE_RUNTIME_CONVERGENCE_MAX_AGE_MS
      ) {
        window.sessionStorage.removeItem(
          TREE_RUNTIME_CONVERGENCE_SESSION_KEY,
        );

        return null;
      }

      return sanitizeTreeRuntimeConvergenceProjection(
        envelope.projection,
      );
    } catch {
      return null;
    }
  };

export const clearTreeRuntimeConvergenceSessionEvidence =
  (): void => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.sessionStorage.removeItem(
        TREE_RUNTIME_CONVERGENCE_SESSION_KEY,
      );
    } catch {
      // Unavailable session storage fails closed.
    }
  };
